import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { 
  Users, Eye, Clock, RefreshCcw, TrendingUp, Activity, Globe, 
  User, Monitor, Smartphone, Calendar, Search, Download, Filter,
  ChevronRight, MapPin, Wifi, WifiOff, BarChart2, ArrowUpRight,
  History, FileText, Play, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminOnline = ({ onSelectStudent }) => {
  const [activeTab, setActiveTab] = useState('live');
  const [onlineData, setOnlineData] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const [actionFilter, setActionFilter] = useState('all');
  const [logsPage, setLogsPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  const loadOnlineData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/online-dashboard`, { withCredentials: true });
      setOnlineData(res.data);
    } catch (err) {
      console.error('Online data error:', err);
    }
  }, []);

  const loadActivityLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: 50,
        page: logsPage,
        date_filter: dateFilter,
        action_filter: actionFilter,
        search: searchTerm
      });
      const res = await axios.get(`${API}/admin/activity-logs?${params}`, { withCredentials: true });
      setActivityLogs(res.data.logs || []);
      setTotalLogs(res.data.total || 0);
    } catch (err) {
      console.error('Activity logs error:', err);
    }
  }, [logsPage, dateFilter, actionFilter, searchTerm]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadOnlineData(), loadActivityLogs()]);
      setLoading(false);
    };
    loadAll();
  }, [loadOnlineData, loadActivityLogs]);

  useEffect(() => {
    if (autoRefresh && activeTab === 'live') {
      const interval = setInterval(loadOnlineData, 10000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, activeTab, loadOnlineData]);

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return '-';
    const now = new Date();
    const then = new Date(isoString);
    const diff = Math.floor((now - then) / 1000);
    if (diff < 60) return `${diff} sn önce`;
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor className="w-4 h-4" />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('iphone') || ua.includes('android')) {
      return <Smartphone className="w-4 h-4" />;
    }
    return <Monitor className="w-4 h-4" />;
  };

  const getActionIcon = (action) => {
    switch(action) {
      case 'login': return <ArrowUpRight className="w-4 h-4 text-green-500" />;
      case 'page_view': return <Eye className="w-4 h-4 text-blue-500" />;
      case 'lesson_complete': return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'quiz_submit': return <FileText className="w-4 h-4 text-purple-500" />;
      case 'video_watch': return <Play className="w-4 h-4 text-orange-500" />;
      case 'logout': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'login': 'Giriş Yaptı',
      'logout': 'Çıkış Yaptı',
      'page_view': 'Sayfa Görüntüleme',
      'lesson_complete': 'Ders Tamamladı',
      'quiz_submit': 'Quiz Çözdü',
      'video_watch': 'Video İzledi',
      'speaking_practice': 'Speaking Pratik',
      'listening_practice': 'Listening Pratik',
      'exam_complete': 'Sınav Tamamladı',
      'certificate_generated': 'Sertifika Aldı',
      'spin_wheel': 'Çark Çevirdi',
      'profile_update': 'Profil Güncelledi'
    };
    return labels[action] || action;
  };

  const exportLogs = async () => {
    try {
      const res = await axios.get(`${API}/admin/activity-logs/export?date_filter=${dateFilter}`, { 
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `aktivite_raporu_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center gap-4 border-b pb-4">
        <button
          onClick={() => setActiveTab('live')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'live' 
              ? 'bg-green-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="tab-live"
        >
          <Wifi className="w-4 h-4" />
          Canlı İzleme
          {onlineData?.online_count > 0 && (
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
              {onlineData.online_count}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'logs' 
              ? 'bg-blue-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="tab-logs"
        >
          <History className="w-4 h-4" />
          Aktivite Geçmişi
          <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">
            {totalLogs}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab === 'stats' 
              ? 'bg-purple-500 text-white' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
          data-testid="tab-stats"
        >
          <BarChart2 className="w-4 h-4" />
          İstatistikler
        </button>
      </div>

      {/* LIVE TAB */}
      {activeTab === 'live' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <h2 className="text-xl font-bold text-gray-800">Şu Anda Online</h2>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-600">
                <input 
                  type="checkbox" 
                  checked={autoRefresh} 
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                  data-testid="auto-refresh-toggle"
                />
                Otomatik Yenile (10sn)
              </label>
              <Button onClick={loadOnlineData} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCcw className="w-4 h-4 mr-2" />
                Yenile
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Şu An Online</p>
                    <p className="text-3xl font-bold">{onlineData?.online_count || 0}</p>
                  </div>
                  <Wifi className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Bugün Giriş</p>
                    <p className="text-3xl font-bold">{onlineData?.unique_visitors || 0}</p>
                  </div>
                  <Users className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Sayfa Görüntüleme</p>
                    <p className="text-3xl font-bold">{onlineData?.total_pageviews || 0}</p>
                  </div>
                  <Eye className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100 text-sm">Aktivite</p>
                    <p className="text-3xl font-bold">{onlineData?.today_activities?.length || 0}</p>
                  </div>
                  <Activity className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Online Users List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-green-500" />
                Online Kullanıcılar ({onlineData?.online_users?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {onlineData?.online_users?.length > 0 ? (
                <div className="space-y-2">
                  {onlineData.online_users.map((user, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-all"
                      onClick={() => onSelectStudent && onSelectStudent(user.user_id)}
                      data-testid={`online-user-${idx}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{user.user_name || user.user_email || 'Anonim'}</p>
                          <p className="text-sm text-gray-500">{user.user_email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1 text-gray-500">
                          {getDeviceIcon(user.user_agent)}
                        </div>
                        <div className="flex items-center gap-1 text-blue-600">
                          <Globe className="w-4 h-4" />
                          <span className="max-w-[150px] truncate">{user.current_page || '/'}</span>
                        </div>
                        <div className="flex items-center gap-1 text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>{formatTimeAgo(user.last_seen)}</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <WifiOff className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Şu anda online kullanıcı yok</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Important Actions Today */}
          {onlineData?.important_actions?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                  Bugünkü Önemli İşlemler
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {onlineData.important_actions.map((action, idx) => (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg text-center">
                      <span className="text-2xl mb-2 block">{action.icon}</span>
                      <p className="text-2xl font-bold text-gray-800">{action.count}</p>
                      <p className="text-sm text-gray-500">{action.action}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* LOGS TAB */}
      {activeTab === 'logs' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <select 
                value={dateFilter}
                onChange={(e) => { setDateFilter(e.target.value); setLogsPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="date-filter"
              >
                <option value="today">Bugün</option>
                <option value="yesterday">Dün</option>
                <option value="week">Son 7 Gün</option>
                <option value="month">Son 30 Gün</option>
                <option value="all">Tümü</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select 
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setLogsPage(1); }}
                className="px-3 py-2 border rounded-lg text-sm"
                data-testid="action-filter"
              >
                <option value="all">Tüm Aktiviteler</option>
                <option value="login">Girişler</option>
                <option value="page_view">Sayfa Görüntüleme</option>
                <option value="lesson_complete">Ders Tamamlama</option>
                <option value="quiz_submit">Quiz Çözümleri</option>
                <option value="exam_complete">Sınav Tamamlama</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Kullanıcı ara..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setLogsPage(1); }}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Button onClick={loadActivityLogs} variant="outline" size="sm">
              <RefreshCcw className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button onClick={exportLogs} variant="outline" size="sm" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Dışa Aktar
            </Button>
          </div>

          {/* Logs Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">Zaman</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">Kullanıcı</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">Aktivite</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">Sayfa</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">Cihaz</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600">IP</th>
                      <th className="text-left p-4 text-sm font-medium text-gray-600"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLogs.map((log, idx) => (
                      <tr 
                        key={idx} 
                        className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => onSelectStudent && onSelectStudent(log.user_id)}
                        data-testid={`log-row-${idx}`}
                      >
                        <td className="p-4">
                          <div className="text-sm font-medium text-gray-800">{formatTime(log.timestamp)}</div>
                          <div className="text-xs text-gray-500">{log.date}</div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm font-medium text-blue-600">
                              {log.user_name?.charAt(0) || log.email?.charAt(0) || '?'}
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-800 truncate max-w-[150px]">
                                {log.user_name || 'Anonim'}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-[150px]">{log.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action)}
                            <span className="text-sm">{getActionLabel(log.action)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                            {log.page || '-'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-1 text-gray-500">
                            {getDeviceIcon(log.user_agent)}
                            <span className="text-xs">{log.browser || '-'}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <span className="text-xs text-gray-500 font-mono">{log.ip_address?.substring(0, 15) || '-'}</span>
                        </td>
                        <td className="p-4">
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalLogs > 50 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <span className="text-sm text-gray-500">
                    Toplam {totalLogs} kayıt
                  </span>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                      disabled={logsPage === 1}
                    >
                      Önceki
                    </Button>
                    <span className="text-sm text-gray-600">Sayfa {logsPage}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setLogsPage(p => p + 1)}
                      disabled={activityLogs.length < 50}
                    >
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* STATS TAB */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Activity Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Aktivite Dağılımı (Bugün)</CardTitle>
              </CardHeader>
              <CardContent>
                {onlineData?.activity_breakdown?.length > 0 ? (
                  <div className="space-y-3">
                    {onlineData.activity_breakdown.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getActionIcon(item._id)}
                          <span className="text-sm">{getActionLabel(item._id)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ 
                                width: `${Math.min(100, (item.count / (onlineData.activity_breakdown[0]?.count || 1)) * 100)}%` 
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{item.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Bugün aktivite yok</p>
                )}
              </CardContent>
            </Card>

            {/* Popular Pages */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">En Çok Ziyaret Edilen Sayfalar</CardTitle>
              </CardHeader>
              <CardContent>
                {onlineData?.popular_pages?.length > 0 ? (
                  <div className="space-y-3">
                    {onlineData.popular_pages.slice(0, 8).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                            {idx + 1}
                          </span>
                          <span className="text-sm truncate max-w-[180px]">{item._id || '/'}</span>
                        </div>
                        <span className="text-sm font-medium text-blue-600">{item.count} görüntüleme</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Veri yok</p>
                )}
              </CardContent>
            </Card>

            {/* Hourly Activity Chart */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Saatlik Aktivite (Son 24 Saat)</CardTitle>
              </CardHeader>
              <CardContent>
                {onlineData?.hourly_data?.length > 0 ? (
                  <div className="flex items-end gap-1 h-32">
                    {Array.from({ length: 24 }, (_, i) => {
                      const hour = String(i).padStart(2, '0');
                      const data = onlineData.hourly_data.find(d => d._id === hour);
                      const count = data?.count || 0;
                      const maxCount = Math.max(...onlineData.hourly_data.map(d => d.count || 0), 1);
                      const height = (count / maxCount) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center">
                          <div 
                            className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600" 
                            style={{ height: `${Math.max(height, 4)}%` }}
                            title={`${hour}:00 - ${count} aktivite`}
                          />
                          {i % 4 === 0 && (
                            <span className="text-xs text-gray-500 mt-1">{hour}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-8">Veri yok</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOnline;
